import { Component } from '@theme/component';

const normalize = value => String(value || '').trim().toLowerCase();
const entries = value => String(value || '').split(',').map(normalize).filter(Boolean);

export function matchesPromo(product, rule) {
  if (!rule.enabled || !String(rule.text || '').trim()) return false;
  if ((rule.excluded || []).includes(product.handle)) return false;
  const checks = [];
  if (rule.products?.length) checks.push(rule.products.includes(product.handle));
  if (rule.collections?.length) checks.push(rule.collections.some(handle => (product.collections || []).includes(handle)));
  const tags = entries(rule.tags);
  if (tags.length) checks.push(tags.some(tag => (product.tags || []).some(value => normalize(value) === tag)));
  const vendors = entries(rule.vendors);
  if (vendors.length) checks.push(vendors.includes(normalize(product.vendor)));
  const types = entries(rule.types);
  if (types.length) checks.push(types.includes(normalize(product.type)));
  return checks.length > 0 && (rule.match === 'all' ? checks.every(Boolean) : checks.some(Boolean));
}

class NFFPromoBadges extends Component {
  #observer;
  #listeners;
  #frame;
  #config;
  #painted = new WeakMap();

  connectedCallback() {
    super.connectedCallback();
    this.#prepare();
  }

  updatedCallback() {
    super.updatedCallback();
    this.#prepare();
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#observer?.disconnect();
    this.#listeners?.abort();
    cancelAnimationFrame(this.#frame);
    this.#clear();
  }

  #prepare() {
    this.#observer?.disconnect();
    this.#listeners?.abort();
    cancelAnimationFrame(this.#frame);
    try { this.#config = JSON.parse(this.querySelector('template[data-nff-promo-config]').content.textContent); }
    catch { return; }
    this.#painted = new WeakMap();
    this.#listeners = new AbortController();
    const schedule = () => this.#schedule();
    document.addEventListener('shopify:section:load', schedule, { signal: this.#listeners.signal });
    document.addEventListener('shopify:block:select', schedule, { signal: this.#listeners.signal });
    this.#observer = new MutationObserver(records => {
      if (records.some(record => {
        if (record.target instanceof Element && record.target.closest('[data-nff-promo-owner]')) return false;
        if (record.type === 'attributes') return true;
        return [...record.addedNodes, ...record.removedNodes].some(node => node instanceof Element && !node.matches('[data-nff-promo-owner]'));
      })) this.#schedule();
    });
    this.#observer.observe(document.body, { childList: true, subtree: true, attributes: true, attributeFilter: ['data-nff-promo-product'] });
    this.#schedule();
  }

  #schedule() {
    cancelAnimationFrame(this.#frame);
    this.#frame = requestAnimationFrame(() => {
      for (const root of document.querySelectorAll('[data-nff-promo-product]')) this.#paint(root);
    });
  }

  #clear() {
    for (const element of document.querySelectorAll('[data-nff-promo-owner]')) {
      if (element.dataset.nffPromoOwner !== this.dataset.section) continue;
      const target = element.parentElement;
      element.remove();
      if (!target.querySelector('[data-nff-promo-owner]')) target.classList.remove('nff-promo-target', 'nff-promo-target--replace-sale');
    }
  }

  #paint(root) {
    const kind = root.dataset.nffPromoKind;
    const target = root.querySelector(kind === 'product' ? '.product-information__media' : '.card-gallery, .nff-photo');
    if (!target) return;
    const source = root.getAttribute('data-nff-promo-product');
    const cached = this.#painted.get(root);
    if (cached?.source === source && cached.target === target && (!cached.container || cached.container.isConnected)) return;
    let product;
    try { product = JSON.parse(source); } catch { return; }
    for (const existing of target.querySelectorAll('[data-nff-promo-owner]')) {
      if (existing.dataset.nffPromoOwner === this.dataset.section) existing.remove();
    }
    const enabled = this.#config.enabled && (kind === 'product' ? this.#config.productPages : this.#config.cards);
    const matching = enabled ? this.#config.rules.filter(rule => matchesPromo(product, rule)).slice(0, this.#config.maximum) : [];
    if (!matching.length) {
      if (!target.querySelector('[data-nff-promo-owner]')) target.classList.remove('nff-promo-target', 'nff-promo-target--replace-sale');
      this.#painted.set(root, { source, target });
      return;
    }
    const container = document.createElement('div');
    container.className = 'nff-promo-labels';
    container.dataset.nffPromoOwner = this.dataset.section;
    const groups = new Map();
    for (const rule of matching) {
      const position = ['tl','tr','bl','br'].includes(rule.position) ? rule.position : 'tl';
      let group = groups.get(position);
      if (!group) {
        group = document.createElement('div'); group.className = 'nff-promo-stack nff-promo-stack--' + position;
        groups.set(position, group); container.append(group);
      }
      const badge = document.createElement('span');
      badge.className = 'nff-promo-badge';
      badge.textContent = rule.text;
      badge.style.setProperty('--promo-background', rule.bg);
      badge.style.setProperty('--promo-foreground', rule.fg);
      badge.style.setProperty('--promo-size', Math.min(18, Math.max(10, Number(rule.size) || 11)) + 'px');
      badge.style.setProperty('--promo-radius', rule.shape === 'pill' ? '999px' : rule.shape === 'rounded' ? '5px' : '0px');
      if (rule.uppercase) badge.style.textTransform = 'uppercase';
      group.append(badge);
    }
    target.classList.add('nff-promo-target');
    target.classList.toggle('nff-promo-target--replace-sale', this.#config.replaceSale && product.available);
    target.append(container);
    this.#painted.set(root, { source, target, container });
  }
}

if (!customElements.get('nff-promo-badges')) customElements.define('nff-promo-badges', NFFPromoBadges);
